import { useCallback, useEffect, useRef, useState, MouseEvent, PointerEvent } from 'react'
import { cn } from '../../lib/utils'
import { MoreKeysPopover } from './MoreKeysPopover'

interface MobileKeyboardBarProps {
  onSendData: (data: string) => void
  visible: boolean
}

const HOLD_INITIAL_DELAY_MS = 350
const HOLD_REPEAT_INTERVAL_MS = 60

const ARROW_KEYS = {
  up: '\x1b[A',
  down: '\x1b[B',
  left: '\x1b[D',
  right: '\x1b[C',
} as const

/**
 * Mobile keyboard bar with extra terminal control keys.
 *
 * **Positioning model: in-flow `flex-shrink-0`, NOT fixed.**
 *
 * The previous fixed-position implementation computed `bottom: <offset>` from
 * the visualViewport API, which behaved differently on iOS 16.4+ vs earlier
 * versions — causing either a visible gap above the keyboard or the bar
 * overlaying the terminal content. The flow-based model relies on the page
 * root being clamped to visualViewport.height (in Workspace.tsx) so that the
 * end of the flex column sits at the keyboard top by construction. No
 * version-specific math required.
 *
 * Layout: [Esc] [Tab] [↑ ↓ ← → d-pad] [Ctrl+C] [⋯ More]
 *   - All four arrows visible by default; d-pad supports press-and-hold repeat.
 *   - Every button uses onMouseDown preventDefault so it does NOT steal focus
 *     from the xterm hidden textarea — the iOS soft keyboard stays open.
 */
export function MobileKeyboardBar({
  onSendData,
  visible,
}: MobileKeyboardBarProps) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  // Tracks the most-recently pasted text — kept for parity with the old API
  // but no longer used by Clear (which now sends Ctrl-U). Some future Clear
  // variant may want it back.
  const lastPastedRef = useRef<string>('')

  // Don't steal focus — keep iOS keyboard open through every interaction
  const preventBlur = useCallback((e: MouseEvent) => e.preventDefault(), [])

  const sendKey = useCallback(
    (data: string) => onSendData(data),
    [onSendData],
  )

  const handlePopoverSend = useCallback(
    (data: string) => onSendData(data),
    [onSendData],
  )

  const closePopover = useCallback(() => setPopoverOpen(false), [])

  if (!visible) return null

  // touch-manipulation maps to CSS `touch-action: manipulation` — disables
  // the iOS double-tap-to-zoom delay AND tells iOS to suppress the synthetic
  // mouse-event sequence that can otherwise leak through after a touch and
  // blur the xterm hidden textarea (closing the soft keyboard).
  const baseBtn =
    'min-w-[44px] min-h-[44px] px-2 py-2 rounded-md text-sm font-medium ' +
    'flex items-center justify-center transition-colors touch-manipulation ' +
    'bg-secondary hover:bg-secondary/80 active:bg-secondary/60 text-secondary-foreground'

  return (
    // `relative` so the popover panel can position itself directly above the
    // bar via `bottom: 100%` rather than via fragile fixed-position math.
    // Bar wrapper also sets touch-manipulation + preventDefault on pointerdown
    // so a tap landing in the gap/padding between buttons doesn't fall through
    // and blur the xterm textarea.
    <div className="relative shrink-0">
      <div
        onPointerDown={(e) => e.preventDefault()}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 touch-manipulation',
          'bg-card border-t border-border',
        )}
      >
        <button
          type="button"
          onMouseDown={preventBlur}
          onClick={() => sendKey('\x1b')}
          className={cn(baseBtn, 'flex-1')}
        >
          Esc
        </button>
        <button
          type="button"
          onMouseDown={preventBlur}
          onClick={() => sendKey('\t')}
          className={cn(baseBtn, 'flex-1')}
        >
          Tab
        </button>
        <DPad onArrow={sendKey} />
        <button
          type="button"
          onMouseDown={preventBlur}
          onClick={() => sendKey('\x03')}
          className={cn(baseBtn, 'flex-1')}
        >
          Ctrl+C
        </button>
        <button
          type="button"
          onMouseDown={preventBlur}
          onClick={() => setPopoverOpen((prev) => !prev)}
          className={cn(
            baseBtn,
            'flex-1',
            popoverOpen && 'bg-primary text-primary-foreground hover:bg-primary/90',
          )}
          aria-label="More keys"
        >
          ⋯
        </button>
      </div>
      <MoreKeysPopover
        open={popoverOpen}
        onClose={closePopover}
        onSendKey={handlePopoverSend}
        lastPastedRef={lastPastedRef}
      />
    </div>
  )
}

/**
 * D-pad cluster: 4 directional arrows in a single visual unit.
 * Tap = single key. Press-and-hold = auto-repeat after a short initial delay.
 *
 * Uses Pointer Events (single source of truth across mouse, touch, pen).
 * Pointerdown.preventDefault() suppresses iOS Safari's synthetic mousedown +
 * click that would otherwise fire after touchend — without that suppression,
 * each tap was sending its arrow byte twice on real devices. preventDefault
 * also keeps focus on the xterm hidden textarea (iOS soft keyboard stays up).
 */
function DPad({ onArrow }: { onArrow: (data: string) => void }) {
  const initialTimerRef = useRef<number | null>(null)
  const repeatTimerRef = useRef<number | null>(null)

  const stopRepeat = useCallback(() => {
    if (initialTimerRef.current !== null) {
      window.clearTimeout(initialTimerRef.current)
      initialTimerRef.current = null
    }
    if (repeatTimerRef.current !== null) {
      window.clearInterval(repeatTimerRef.current)
      repeatTimerRef.current = null
    }
  }, [])

  const startRepeat = useCallback(
    (data: string) => {
      onArrow(data)
      stopRepeat()
      initialTimerRef.current = window.setTimeout(() => {
        repeatTimerRef.current = window.setInterval(() => {
          onArrow(data)
        }, HOLD_REPEAT_INTERVAL_MS)
      }, HOLD_INITIAL_DELAY_MS)
    },
    [onArrow, stopRepeat],
  )

  useEffect(() => stopRepeat, [stopRepeat])

  const arrowBtn = (label: string, data: string, ariaLabel: string) => (
    <button
      type="button"
      aria-label={ariaLabel}
      onPointerDown={(e: PointerEvent) => {
        e.preventDefault()
        startRepeat(data)
      }}
      onPointerUp={stopRepeat}
      onPointerCancel={stopRepeat}
      onPointerLeave={stopRepeat}
      // Belt-and-suspenders: some iOS Safari versions still fire a synthetic
      // mousedown after a touch even when pointerdown.preventDefault() was
      // called. Suppressing focus shift here keeps the xterm soft keyboard up.
      onMouseDown={(e) => e.preventDefault()}
      className={
        'flex-1 min-h-[44px] px-1 rounded text-sm font-medium ' +
        'flex items-center justify-center transition-colors touch-manipulation ' +
        'bg-secondary/50 hover:bg-secondary active:bg-secondary/40 text-secondary-foreground'
      }
    >
      {label}
    </button>
  )

  return (
    <div
      onPointerDown={(e) => e.preventDefault()}
      className={
        'flex items-center gap-0.5 flex-[2] min-w-[120px] touch-manipulation ' +
        'rounded-md bg-secondary/30 px-0.5'
      }
    >
      {arrowBtn('↑', ARROW_KEYS.up, 'Up arrow')}
      {arrowBtn('↓', ARROW_KEYS.down, 'Down arrow')}
      {arrowBtn('←', ARROW_KEYS.left, 'Left arrow')}
      {arrowBtn('→', ARROW_KEYS.right, 'Right arrow')}
    </div>
  )
}
